// @flow
import type { Theme } from '@mui/material/styles';

type StyleObj = { [key: string]: mixed };
type StyleFn = (theme: Theme) => StyleObj;
type Styles = {
  [key: string]: StyleFn
};

const COL_REQUEST_MIN_WIDTH = 200;
const COL_REQUEST_WIDTH = 200;
const COL_TYPE_WIDTH = 250;
const COL_TAG_WIDTH = 300;
const COL_CUSTOMER_WIDTH = 250;
const COL_PROPERTY_WIDTH = 225;
const COL_UNIT_WIDTH = 150;
const COL_COUNT_WIDTH = 80;
const COL_STATUS_WIDTH = 150;
const COL_ASSIGNEE_WIDTH = 150;
const COL_DATE_WIDTH = 159;
const COL_ACTION_WIDTH = 183;
const ROW_CELL_BACKGROUND_COLOR = 'hsl(0,0%,99%)';

// CSS Grid layout constants
// Note: No action column - action buttons overlay on hover
const GRID_COLUMNS = {
  customer: '250px',
  tag: 'minmax(150px, 1fr)',  // Flexible: min 150px, grows to fill remaining space
  subject: '200px',
  staff: '130px',
  date: '130px'
};

// Grid template for desktop (default)
const DESKTOP_GRID_TEMPLATE = `${GRID_COLUMNS.customer} ${GRID_COLUMNS.tag} ${GRID_COLUMNS.subject} ${GRID_COLUMNS.staff} ${GRID_COLUMNS.date}`;

// Grid template for smaller screens (lg breakpoint and below)
const RESPONSIVE_GRID_TEMPLATE = '220px minmax(120px, 1fr) 180px 110px 110px';

const feedTableStyles: Styles = {
  table: (theme) => ({
    tableLayout: 'fixed',
    borderCollapse: 'separate',
    borderSpacing: 0,
    padding: 0,
    width: '100%',
    display: 'block',
    whiteSpace: 'nowrap',
    '& thead, & tbody': {
      display: 'block'
    },
    '& th': {
      display: 'flex'
    },
    '& tr:first-of-type': {
      borderTop: 'none'
    }
  }),

  feedTableBody: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    width: `calc(100vw - ${theme.layout.customProps.drawer.width}px - ${theme.spacing(6)}px)`,
    [theme.breakpoints.down('sm')]: {
      width: `calc(100vw - ${theme.layout.customProps.drawer.width}px - ${theme.spacing(2)}px)`,
      minWidth: '100%'
    }
  }),

  cell: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: ROW_CELL_BACKGROUND_COLOR,
    padding: theme.spacing(0),
    height: theme.layout.listTable.rowHeight,
    borderBottom: 0
  }),

  requestCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_REQUEST_MIN_WIDTH,
    maxWidth: COL_REQUEST_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  typeCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_TYPE_WIDTH,
    maxWidth: COL_TYPE_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  customerCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_CUSTOMER_WIDTH,
    maxWidth: COL_CUSTOMER_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  countCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_COUNT_WIDTH,
    maxWidth: COL_COUNT_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  propertyCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_PROPERTY_WIDTH,
    maxWidth: COL_PROPERTY_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  unitCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_UNIT_WIDTH,
    maxWidth: COL_UNIT_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  statusCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_STATUS_WIDTH,
    maxWidth: COL_STATUS_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR

    // width: '1%',
    // whiteSpace: 'nowrap',
    // overflow: 'hidden',
    // whiteSpace: 'nowrap',
    // display: 'table-cell',
    // position: 'relative',
    // width: '20px',
    // backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  dateCell: (theme) => ({
    flex: '0 0 auto',
    paddingRight: theme.spacing(3),
    minWidth: COL_DATE_WIDTH,
    maxWidth: COL_DATE_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  assigneeCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_ASSIGNEE_WIDTH,
    maxWidth: COL_ASSIGNEE_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  actionCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_ACTION_WIDTH,
    maxWidth: COL_ACTION_WIDTH,
    paddingRight: 0,
    paddingLeft: 0,
    width: 'auto',
    marginLeft: theme.spacing(0.375),
    backgroundColor: 'transparent',  // No background on the td
    position: 'relative',
    '& button': {
      // normal button styles here
      backgroundColor: 'transparent',
    },
    // Now add a :hover rule to color only the button or a child
    '&:hover button': {
      backgroundColor: theme.palette.primary.lighter,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
    // '& button': {
    //   backgroundColor: ROW_CELL_BACKGROUND_COLOR
    // }
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  broadcastActionCell: (theme) => ({
    marginLeft: theme.spacing(18.375),
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  feedTableHeadRow: (theme) => ({
    display: 'flex',
    position: 'relative',
    height: 30,
    '& th': {
      fontSize: '0.8rem',
      fontWeight: 400,
      color: theme.palette.custom.backgroundTextInfo,
      padding: theme.spacing(1),
      borderBottom: 0,
      backgroundColor: 'transparent'
    }
  }),

  '@keyframes bounce': {
    'from, 20%, 53%, 80%, to': {
      transform: 'translate3d(0, 0, 0)'
    },
    '40%, 43%': {
      animationTimingFunction: 'cubic-bezier(0.755, 0.05, 0.855, 0.06)',
      transform: 'translate3d(0, -30px, 0)'
    },
    '70%': {
      animationTimingFunction: 'cubic-bezier(0.755, 0.05, 0.855, 0.06)',
      transform: 'translate3d(0, -15px, 0)'
    },
    '90%': {
      transform: 'translate3d(0, -4px, 0)'
    }
  },

  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translate3d(0, 100%, 0)'
    },
    to: {
      opacity: 1,
      transform: 'translate3d(0, 0, 0)'
    }
  },

  '@keyframes fadeInDown': {
    from: {
      opacity: 0,
      transform: 'translate3d(0, -100%, 0)'
    },
    to: {
      opacity: 1,
      transform: 'translate3d(0, 0, 0)'
    }
  },

  '@keyframes fadeIn': {
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  },

  feedTableAddedEnter: () => ({
    opacity: 0
  }),

  feedTableAddedEnterActive: () => ({
    animationName: '$fadeInDown',
    animationDuration: '1s',
    animationIterationCount: '1',
    transformOrigin: 'center top'
  }),

  feedTableUpdatedEnter: () => ({
    opacity: 0
  }),

  feedTableUpdatedEnterActive: () => ({
    animationName: '$fadeInUp',
    animationDelay: '0.3s',
    animationDuration: '1s',
    animationIterationCount: '1',
    transformOrigin: 'center bottom'
  }),

  feedTablePushedinEnter: () => ({
    opacity: 0
  }),

  feedTablePushedinEnterActive: () => ({
    animationName: '$fadeIn',
    animationDelay: '1s',
    animationDuration: '1.5s',
    animationFillMode: 'both',
    animationIterationCount: '1'
  }),

  unreadBadgeCtr: (theme) => ({
    marginLeft: theme.spacing(1.5)
  }),

  unreadBadgeShowing: (theme) => ({
    marginRight: theme.spacing(1)
  }),

  unreadFilter: (theme) => ({
    textDecoration: 'underline',
    marginLeft: theme.spacing(1),
    cursor: 'pointer'
  }),

  colRequest: (theme) => ({
    width: COL_REQUEST_WIDTH,
    minWidth: COL_REQUEST_MIN_WIDTH
  }),

  colType: (theme) => ({
    width: COL_TYPE_WIDTH
  }),

  colTag: (theme) => ({
    width: COL_TAG_WIDTH
  }),

  colCustomerName: (theme) => ({
    width: COL_CUSTOMER_WIDTH
  }),

  colProperty: (theme) => ({
    width: COL_PROPERTY_WIDTH
  }),

  colUnit: (theme) => ({
    width: COL_UNIT_WIDTH
  }),

  colCount: (theme) => ({
    width: COL_COUNT_WIDTH
  }),

  colStatus: (theme) => ({
    width: COL_STATUS_WIDTH
  }),

  colAssignee: (theme) => ({
    width: COL_ASSIGNEE_WIDTH
  }),

  colDate: (theme) => ({
    width: COL_DATE_WIDTH
  }),

  colAction: (theme) => ({
    width: COL_ACTION_WIDTH
  }),

  customerNameCell: (theme) => ({
    flex: '0 0 auto',
    minWidth: COL_CUSTOMER_WIDTH,
    maxWidth: COL_CUSTOMER_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR
  }),

  tagCell: (theme) => ({
    flex: '1 1',
    display: 'flex',
    minWidth: COL_TAG_WIDTH,
    // maxWidth: COL_TAG_WIDTH,
    backgroundColor: ROW_CELL_BACKGROUND_COLOR,
    overflow: 'hidden',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }),

  textOverflow: (theme) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }),

  // ============================================
  // CSS Grid Layout Styles (for responsive table)
  // ============================================

  // Grid container - wraps entire table (header + body)
  feedTableGrid: (theme) => ({
    display: 'grid',
    gridTemplateColumns: DESKTOP_GRID_TEMPLATE,
    width: '100%',
    rowGap: theme.spacing(2),  // Match original row spacing
    // Responsive: smaller columns at lg breakpoint
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: RESPONSIVE_GRID_TEMPLATE
    }
  }),

  // Grid header row
  feedTableGridHeaderRow: (theme) => ({
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gridColumn: '1 / -1',
    height: 30
  }),

  // Grid header cell
  feedTableGridHeaderCell: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.8rem',
    fontWeight: 400,
    color: theme.palette.custom.backgroundTextInfo,
    padding: theme.spacing(0),  // Match data cell padding for alignment
    backgroundColor: 'transparent'
  }),

  // Grid data row
  feedTableGridRow: (theme) => ({
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gridColumn: '1 / -1',
    position: 'relative',
    backgroundColor: ROW_CELL_BACKGROUND_COLOR,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',  // Clip cell backgrounds to rounded corners
    cursor: 'pointer',
    // Paper effect shadow at rest (matches original feedTableRow)
    boxShadow: '0px 2px 8px -1px rgba(0,0,0,0.2)',
    '&:hover': {
      // Darker shadow on hover
      boxShadow: '0px 2px 8px -1px rgba(0,0,0,0.8)'
    }
  }),

  // Grid cell base style
  feedTableGridCell: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0),  // Match original cell padding
    overflow: 'hidden',
    height: theme.layout.listTable.rowHeight,  // Fixed height like original cell
    backgroundColor: ROW_CELL_BACKGROUND_COLOR,
    // Match MUI TableCell typography (body2)
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight
  }),

  // Grid cell - first cell with left margin
  // Border radius removed - row's overflow: hidden handles clipping
  feedTableGridCellFirst: (theme) => ({
    paddingLeft: theme.spacing(1)  // Match original row left margin
  }),

  // Grid cell - last cell (border radius handled by row's overflow: hidden)
  feedTableGridCellLast: (theme) => ({}),

  // Tag cell for grid - enables horizontal scroll for tags
  tagGridCell: (theme) => ({
    overflow: 'hidden',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& > *': {
      overflowX: 'auto',
      overflowY: 'hidden',
      width: '100%',
      // Thin scrollbar styling
      '&::-webkit-scrollbar': {
        height: 4
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'transparent',
        borderRadius: 2
      },
      '&:hover::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.3)'
      }
    }
  }),

  // Action cell for grid - contains absolute positioned overlay
  actionGridCell: (theme) => ({
    position: 'relative',
    overflow: 'visible',
    padding: 0
  }),

  // Action buttons overlay - appears on row hover
  actionOverlay: (theme) => ({
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.15s ease',
    backgroundColor: theme.palette.primary.lighter,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    zIndex: 10,
    // No paddingRight - background should fill to row's right edge
    '& button': {
      color: 'white',
      // Button should match row corner radius on right side for seamless blend
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0
    }
  }),

  // Action overlay visible state (applied when row is hovered)
  actionOverlayVisible: (theme) => ({
    opacity: 1,
    visibility: 'visible'
  }),

  feedTableExit: () => ({
    opacity: 1
  }),

  feedTableExitActive: () => ({
    opacity: 0
  }),

  feedTableAppear: () => ({
    opacity: 0.01
  }),

  feedTableAppearActive: () => ({
    opacity: 1,
    transition: 'opacity 500ms ease-in'
  })
};

export default feedTableStyles;